<template>
  <div class="container-fluid">
    <section id="summary" class="py-2">
      <div id="credit-explainer" class="px-3">
        <div
          class="w-100 d-flex flex-column align-items-center justify-content-center flex-fill"
        >
          <img
            :src="useAsset('img/credit-line/big_coin.png')"
            alt="big coin"
            class="mt-3 mb-1 mx-auto"
          />
        </div>
        <h5 class="py-0 mt-4 text-center" id="approved-amount">
          {{ t("CL_CONTRACT.APPROVED_CREDIT") }}
          <br />
          <span class="text-center fs-1">{{
            currencyFormat(userOffer.approvedCreditLine)
          }}</span>
        </h5>
        <div class="container items-summary" style="z-index: 100000000000">
          <div class="row">
            <div class="col-8 text-start">
              {{ t("CL_CONTRACT.COST_USE_IT") }}
            </div>
            <div class="col-4 text-end fw-bold">$0</div>
          </div>
          <div v-if="shouldShowDynamicFields">
            <div class="row" v-for="field in dynamicFields" :key="field.text">
              <div class="col-8 text-start">
                {{ field.text }}
              </div>
              <div class="col-4 text-end fw-bold">
                {{ field.value }}
              </div>
            </div>
          </div>
        </div>
      </div>
      <img
        :src="useAsset('img/credit-line/coins.png')"
        alt="coins"
        class="position-absolute top-0 z-3 coins-img"
      />
    </section>

    <section class="mt-4 w-100 px-0">
      <div class="d-flex mt-4 flex-fill" v-for="i in 3" :key="i">
        <div class="ms-0 me-3">
          <img
            alt="checkmark"
            :src="`${useAsset('img/credit-line/checkmark.png')}`"
          />
        </div>
        <div
          class="d-flex align-items-center check-legend"
          v-html="t(`NEW_CL_OFFER_ITEMS.${i}`)"
        ></div>
      </div>
    </section>

    <section class="w-100 mx-auto flex-fill mt-5 pt-3">
      <Button
        class="accept-offer-btn mb-3"
        @click="acceptOffer"
        id="accept-offer-btn"
      >
        {{ t("NEW_CL_OFFER_ITEMS.ACCEPT_OFFER") }}
      </Button>

      <Button
        class="reject-link mt-2"
        @click="rejectOffer"
        id="reject-offer-btn"
      >
        {{ t("NEW_CL_OFFER_ITEMS.NOT_INTERESTED") }}
      </Button>
    </section>
  </div>
</template>

<script lang="ts" setup>
import useAsset from "@/apps/credit-line/helpers/useAsset";
import { useI18n } from "@/apps/credit-line/composables/useI18n";
import Button from "@/apps/credit-line/components/Button.vue";
import { RegistrationSteps } from "@/apps/credit-line/types/registrationSteps";
import logAnalyticsEvent from "@/apps/credit-line/services/google-analytics-events.service";
import AnalyticEvents from "@/shared-helpers/google-analytics-events.helper";
import { IOffer } from "@/apps/credit-line/models/IOffer";
import { computed, inject, Ref } from "vue";
import currencyFormat from "@/apps/credit-line/helpers/currencyFormatter.helper";

const emit = defineEmits<{
  (e: "switchStep", step: RegistrationSteps): void;
  (e: "toggleShowModal"): void;
}>();

const props = defineProps<{
  userOffer: IOffer;
}>();

const { t } = useI18n();
const creditLineOfferFields: Ref = inject("creditLineOfferFields") as Ref;
const dynamicOfferFieldsFactory = inject("dynamicOfferFieldsFactory");

const dynamicFields = computed(() => {
  if (
    !dynamicOfferFieldsFactory ||
    typeof dynamicOfferFieldsFactory.value !== "function"
  ) {
    console.log("❌ No valid dynamicOfferFieldsFactory");
    return [];
  }

  return dynamicOfferFieldsFactory.value(props.userOffer, t);
});

const shouldShowDynamicFields = computed(() => {
  try {
    const hasEnabledSetting = creditLineOfferFields.value?.enabled;
    const hasFields = creditLineOfferFields.value?.values?.fields;
    const hasDynamicFields = dynamicFields.value.length > 0;

    return hasEnabledSetting && hasFields && hasDynamicFields;
  } catch (error) {
    console.log(error);
    return false;
  }
});

const acceptOffer = () => {
  emit("switchStep", RegistrationSteps.ACCEPT);
};

const rejectOffer = () => {
  logAnalyticsEvent(
    AnalyticEvents.CreditLine.ClickNotInterested,
    "Offer",
    "User declines offer",
  );
  emit("switchStep", RegistrationSteps.REJECT);
};
</script>

<style lang="scss" scoped>
@import "@/assets/scss/_constants.scss";

main {
  position: relative;

  & * {
    position: relative;
  }
}

.light-text {
  color: $pj_color-cool-gray;
}

.accept-offer-btn {
  background: $pj-color-green;
  border-radius: 11px;
  font-style: normal;
  font-weight: 600;
  font-size: 16px;
  line-height: 20px;
  text-align: center;
  letter-spacing: 0.8px;
  color: $pj-color-white;
  width: 100%;
  height: 40px;
  border: none;
}

.accept-offer-btn-disabled {
  background-color: $pj-color-gray;
  cursor: not-allowed;
  border-radius: 11px;
  font-style: normal;
  font-weight: 600;
  font-size: 16px;
  line-height: 20px;
  text-align: center;
  letter-spacing: 0.8px;
  color: $pj-color-white;
  width: 90%;
  height: 40px;
  border: none;
}

.check-legend {
  font-style: normal;
  font-weight: 600;
  font-size: 14px;
  line-height: 22px;
  letter-spacing: 0.24px;
}

.payments-card {
  padding: 2.5%;
  border-radius: 10px;
  border-color: $pj-color-light-gray;
  border-width: thin;
  border-style: solid;
}

.reject-link {
  color: $pj-color-green;
  text-align: center;
  font-feature-settings: "clig" off, "liga" off;
  font-family: Inter, serif;
  font-size: 0.875rem;
  font-style: normal;
  background-color: transparent;
  border: none;
  box-shadow: none;
  font-weight: 500;
  line-height: 144%; /* 1.26rem */
}

.option-btn {
  width: 100%;
  height: 50px;
  background-color: $pj-color-white;
  border-radius: 5px;
  color: $pj-color-green;
  text-align: left;
  font-size: 5vw;
  padding-left: 2.5%;
  border-color: $pj-color-green;
  border-width: thin;
}

.disabled {
  background-color: $pj-color-gray;
}

.btn-disabled {
  background-color: $pj-color-gray;
  cursor: not-allowed;
}

.cancel-btn {
  color: $pj-color-green;
  background-color: white;
  border-color: transparent;
}

#preference-checkbox {
  height: 1rem;
  width: 1rem;
}

label {
  cursor: pointer;
  color: $pj-color-green;
  font-weight: bold;
  font-size: medium;
}

.checkbox {
  box-shadow: none;
  outline: none;
  border-color: $pj-color-green;

  &:checked {
    background-color: $pj-color-green;
    color: $pj-color-green;
    outline: none;
    border: 0;
    box-shadow: none;
  }
}

#credit-explainer {
  position: relative;
  padding-top: 1.13rem;
  padding-bottom: 0.81rem;

  & img {
    width: 3.6875rem;
    height: 4.3125rem;
  }
}

.items-summary {
  padding-left: 0 !important;
  padding-right: 0 !important;
}

.items-summary .row {
  margin-top: 0.5rem;

  & div:nth-child(2) {
    color: #666;
    text-align: right;
    font-family: Inter, serif;
    font-size: 0.875rem;
    font-style: normal;
    font-weight: 700;
    letter-spacing: 0.015rem;
  }
}

#approved-amount {
  color: #666;
  text-align: center;
  font-family: Inter, serif;
  font-size: 1.125rem;
  font-style: normal;
  font-weight: 400;
  letter-spacing: 0.01125rem;

  & span {
    color: $pj-color-green;
    font-family: Inter, serif;
    font-size: 2.25rem;
    font-style: normal;
    font-weight: 600;
    line-height: 2.5rem;
    letter-spacing: 0.01125rem;
  }
}

#how-interest-btn {
  color: $pj-color-green;
  text-align: center;
  font-feature-settings: "clig" off, "liga" off;
  font-family: Inter, serif;
  font-size: 0.9375rem;
  font-style: normal;
  font-weight: 600;
  line-height: 1.5rem; /* 160% */
}

#summary {
  background-color: #f6f6f6;
  width: 120%;
  position: relative;
  left: -9%;
}

.coins-img {
  width: 100%;
  z-index: 1000;
  margin-top: 25%;
}

* {
  font-family: Inter, serif !important;
  max-width: 100vw !important;
}
</style>
